import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiClient, formatApiError } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { CARTAL_CONTACT, CARTAL_LABELS } from "../lib/contactInfo";
import { FEATURES } from "../lib/features";
import Seo from "../components/Seo";
import {
  Mail, Phone, MessageSquare, Send, Loader2, CheckCircle2, MapPin, Clock, Copy, Check, HelpCircle, Globe,
} from "lucide-react";

// iter333 — inline brand SVGs for channels not covered by lucide. Keeps
// the dependency surface at zero and the glyphs crisp at any size.
const BrandIcon = ({ name, className = "w-4 h-4" }) => {
  const map = {
    tiktok: (
      <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.1 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.14-.1z" />
      </svg>
    ),
    snapchat: (
      <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
        <path d="M12 2c3.3 0 5.8 2.3 5.8 5.8 0 1 .1 2.1-.1 2.9.2.1.4.1.7.1.6 0 1-.3 1.3-.3.3 0 .5.2.5.5s-.2.6-.8.9c-.5.2-1.3.4-1.5.9-.1.3.1.6.3 1 .9 1.5 2 2 2.9 2.3.3.1.5.3.4.6-.1.5-1.1.9-2.5 1.1-.1.2-.2.9-.4 1.1-.1.2-.3.2-.5.2h-.1c-.4 0-1-.1-1.6-.1-.8 0-1.1 0-1.6.4-.9.6-1.9 1.3-3.5 1.3-1.6 0-2.6-.7-3.5-1.3-.5-.4-.8-.4-1.6-.4-.7 0-1.2.1-1.6.1h-.1c-.3 0-.5-.1-.5-.2-.2-.3-.3-.9-.4-1.1-1.4-.2-2.4-.6-2.5-1.1-.1-.3.2-.5.4-.6.9-.4 2-.9 2.9-2.3.2-.4.4-.7.3-1-.2-.5-1-.6-1.5-.9-.6-.3-.8-.6-.8-.9s.3-.5.5-.5c.3 0 .7.3 1.3.3.3 0 .5-.1.7-.1-.2-.9-.1-2-.1-2.9C6.2 4.3 8.7 2 12 2z" />
      </svg>
    ),
    x: (
      <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
        <path d="M18.244 2H21l-6.52 7.44L22 22h-6.84l-4.78-6.26L4.9 22H2.14l6.98-7.96L2 2h7l4.33 5.72L18.244 2zm-2.4 18h1.98L8.26 4H6.18l9.664 16z" />
      </svg>
    ),
    telegram: (
      <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
        <path d="M21.75 4.5 2.99 11.73c-1.28.5-1.27 1.21-.23 1.52l4.79 1.49 11.08-6.99c.52-.32 1-.15.61.2l-8.97 8.1h-.01l.01.01-.33 4.93c.49 0 .7-.22.98-.49l2.35-2.29 4.88 3.61c.9.5 1.55.24 1.78-.83l3.22-15.19c.33-1.31-.49-1.9-1.4-1.3z" />
      </svg>
    ),
  };
  return map[name] || null;
};

const SUBJECTS = [
  { id: "general", ar: "استفسار عام", en: "General inquiry" },
  { id: "listing", ar: "مشكلة في إعلان", en: "Listing issue" },
  { id: "payment", ar: FEATURES.ESCROW_ENABLED ? "دفع أو ضمان" : "استفسار عن الدفع", en: FEATURES.ESCROW_ENABLED ? "Payment or escrow" : "Payment inquiry" },
  { id: "partner", ar: "شراكة أو تكامل", en: "Partnership / integration" },
  { id: "enterprise-quote", ar: "عرض سعر — باقة Enterprise", en: "Quote — Enterprise plan" },
  { id: "press", ar: "صحافة وإعلام", en: "Press / media" },
  { id: "other", ar: "أخرى", en: "Other" },
];

export default function ContactPage() {
  const { lang } = useI18n();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const initialSubject = SUBJECTS.find(s => s.id === params.get("subject"))?.id || "general";
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    subject: initialSubject,
    message: "",
  });
  // Sync when the user opens the page with a query-string subject change.
  useEffect(() => {
    const sub = SUBJECTS.find(s => s.id === params.get("subject"))?.id;
    if (sub) setForm(f => ({ ...f, subject: sub }));
  }, [params]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(false);
  const [copied, setCopied] = useState("");

  const copy = async (key, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      setTimeout(() => setCopied(""), 1500);
    } catch { /* noop */ }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await apiClient.post("/contact-messages", {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        subject: SUBJECTS.find((s) => s.id === form.subject)?.[lang === "ar" ? "ar" : "en"] || form.subject,
        message: form.message.trim(),
      });
      setDone(true);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-14" data-testid="contact-page">
      <Seo
        title={lang === "ar" ? "تواصل معنا | كارتال" : "Contact Us | CARTAL"}
        description={lang === "ar" ? "تواصل مع فريق كارتال — دعم السيارات والشركاء والشركات، عبر البريد الإلكتروني أو واتساب." : "Reach the CARTAL team — vehicle, partner and business support via email or WhatsApp."}
        canonicalPath="/contact"
      />
      <div className="max-w-3xl">
        <div className="overline overline-gold mb-3">{lang === "ar" ? "تواصل معنا" : "CONTACT"}</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-medium text-[#0a0a0a] leading-[1.05]">
          {lang === "ar" ? "تواصل مع فريق كارتال" : "Talk to the CARTAL team"}
        </h1>
        <p className="text-[#525252] mt-4 text-base sm:text-lg leading-relaxed">
          {lang === "ar"
            ? "نرحب باستفساراتك واقتراحاتك ومقترحات الشراكة. سنرد عادة خلال يوم عمل واحد."
            : "We welcome questions, suggestions, and partnership proposals. We usually reply within one business day."}
        </p>
      </div>

      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-8 mt-10">
        {/* Form */}
        <div className="surface rounded-3xl p-8">
          {done ? (
            <div className="py-10 text-center" data-testid="contact-success">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-700" />
              </div>
              <h2 className="font-display text-2xl text-[#0a0a0a] mb-2">
                {lang === "ar" ? "تم استلام رسالتك" : "Message received"}
              </h2>
              <p className="text-sm text-[#525252] max-w-md mx-auto leading-relaxed">
                {lang === "ar"
                  ? "شكراً لتواصلك مع كارتال. سيقوم أحد أعضاء فريقنا بالرد على بريدك خلال 24 ساعة."
                  : "Thanks for reaching out. A member of our team will reply to your email within 24 hours."}
              </p>
              <Link
                to="/"
                className="btn-primary mt-6 inline-flex"
                data-testid="contact-back-home"
              >
                {lang === "ar" ? "العودة للرئيسية" : "Back to home"}
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "الاسم" : "Your name"} <span className="text-red-600">*</span></label>
                  <input
                    type="text"
                    className="input-dark mt-1"
                    required
                    minLength={2}
                    value={form.name}
                    onChange={(e) => set("name", e.target.value)}
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "البريد الإلكتروني" : "Email"} <span className="text-red-600">*</span></label>
                  <input
                    type="email"
                    className="input-dark mt-1"
                    required
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    data-testid="contact-email"
                  />
                </div>
                <div>
                  <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "رقم الجوال (اختياري)" : "Phone (optional)"}</label>
                  <input
                    type="tel"
                    className="input-dark mt-1"
                    placeholder="+966 5x xxx xxxx"
                    value={form.phone}
                    onChange={(e) => set("phone", e.target.value)}
                    data-testid="contact-phone"
                  />
                </div>
                <div>
                  <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "الموضوع" : "Subject"}</label>
                  <select
                    className="input-dark mt-1"
                    value={form.subject}
                    onChange={(e) => set("subject", e.target.value)}
                    data-testid="contact-subject"
                  >
                    {SUBJECTS.map((s) => (
                      <option key={s.id} value={s.id}>{lang === "ar" ? s.ar : s.en}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs text-[#6b6b6b]">{lang === "ar" ? "رسالتك" : "Your message"} <span className="text-red-600">*</span></label>
                <textarea
                  className="input-dark mt-1"
                  rows={6}
                  required
                  minLength={10}
                  value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                  placeholder={lang === "ar"
                    ? "اشرح استفسارك أو طلبك بإيجاز…"
                    : "Briefly explain your inquiry or request…"}
                  data-testid="contact-message"
                />
              </div>
              {err && <div className="text-sm text-red-600" data-testid="contact-error">{err}</div>}
              <button
                type="submit"
                className="btn-primary w-full justify-center"
                disabled={busy}
                data-testid="contact-submit"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {lang === "ar" ? "إرسال الرسالة" : "Send message"}
              </button>
              <p className="text-[11px] text-[#8a8a8a] text-center leading-relaxed">
                {lang === "ar"
                  ? "يتم تخزين رسائلك بشكل آمن، ولن تُشارك مع أي طرف ثالث. راجع سياسة الخصوصية."
                  : "Messages are stored securely and never shared with third parties. See our privacy policy."}
              </p>
            </form>
          )}
        </div>

        {/* Sidebar */}
        <aside className="space-y-4">
          {/* ── Official channels (national unified + WhatsApp + email) ── */}
          <div className="surface rounded-3xl p-6" data-testid="official-contact-card"
               itemScope itemType="https://schema.org/Organization">
            <meta itemProp="name" content="CARTAL" />
            <meta itemProp="url" content="https://cartal.sa" />
            <div className="overline overline-gold mb-3">
              {lang === "ar" ? "قنوات الدعم الرسمية" : "OFFICIAL SUPPORT"}
            </div>
            <ul className="space-y-4 text-sm" itemProp="contactPoint" itemScope itemType="https://schema.org/ContactPoint">
              <meta itemProp="contactType" content="customer support" />
              <meta itemProp="areaServed" content="SA" />
              <meta itemProp="availableLanguage" content="Arabic, English" />

              {/* WhatsApp Business */}
              <li className="flex items-start gap-3">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#25D366]/15 border border-[#25D366]/40 shrink-0">
                  <MessageSquare className="w-4 h-4 text-[#1f6f3a]" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-[#6b6b6b]">
                    {lang === "ar" ? CARTAL_LABELS.whatsapp.ar : CARTAL_LABELS.whatsapp.en}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <a
                      href={CARTAL_CONTACT.whatsappUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#0a0a0a] font-semibold hover:text-[#8a6f2e]"
                      dir="ltr"
                      data-testid="contact-whatsapp-link"
                    >
                      {CARTAL_CONTACT.whatsappNumber}
                    </a>
                    <button
                      type="button"
                      onClick={() => copy("wa", CARTAL_CONTACT.whatsappNumber)}
                      className="inline-flex items-center gap-1 text-[10px] text-[#8a6f2e] hover:text-[#c9a55c] font-semibold"
                      data-testid="copy-whatsapp"
                      aria-label={lang === "ar" ? "نسخ الرقم" : "Copy number"}
                    >
                      {copied === "wa"
                        ? <><Check className="w-3 h-3" />{lang === "ar" ? "تم" : "Copied"}</>
                        : <><Copy className="w-3 h-3" />{lang === "ar" ? "نسخ" : "Copy"}</>}
                    </button>
                  </div>
                </div>
              </li>

              {/* Direct call (same number) */}
              <li className="flex items-start gap-3" data-testid="contact-call-row">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#fdf9ef] border border-[#c9a55c]/40 shrink-0">
                  <Phone className="w-4 h-4 text-[#8a6f2e]" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-[#6b6b6b]">
                    {lang === "ar" ? CARTAL_LABELS.call.ar : CARTAL_LABELS.call.en}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <a
                      href={CARTAL_CONTACT.callTel}
                      className="text-[#0a0a0a] font-semibold hover:text-[#8a6f2e]"
                      dir="ltr"
                      data-testid="contact-call-link"
                      itemProp="telephone"
                    >
                      {CARTAL_CONTACT.callNumber}
                    </a>
                    <button
                      type="button"
                      onClick={() => copy("call", CARTAL_CONTACT.callNumber)}
                      className="inline-flex items-center gap-1 text-[10px] text-[#8a6f2e] hover:text-[#c9a55c] font-semibold"
                      data-testid="copy-call"
                      aria-label={lang === "ar" ? "نسخ الرقم" : "Copy number"}
                    >
                      {copied === "call"
                        ? <><Check className="w-3 h-3" />{lang === "ar" ? "تم" : "Copied"}</>
                        : <><Copy className="w-3 h-3" />{lang === "ar" ? "نسخ" : "Copy"}</>}
                    </button>
                  </div>
                </div>
              </li>

              {/* Email */}
              <li className="flex items-start gap-3">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#fdf9ef] border border-[#c9a55c]/40 shrink-0">
                  <Mail className="w-4 h-4 text-[#8a6f2e]" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-[#6b6b6b]">
                    {lang === "ar" ? CARTAL_LABELS.supportEmail.ar : CARTAL_LABELS.supportEmail.en}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <a
                      href={CARTAL_CONTACT.supportEmailMailto}
                      className="text-[#0a0a0a] font-semibold hover:text-[#8a6f2e] break-all"
                      data-testid="contact-email-link"
                      itemProp="email"
                    >
                      {CARTAL_CONTACT.supportEmail}
                    </a>
                    <button
                      type="button"
                      onClick={() => copy("email", CARTAL_CONTACT.supportEmail)}
                      className="inline-flex items-center gap-1 text-[10px] text-[#8a6f2e] hover:text-[#c9a55c] font-semibold"
                      data-testid="copy-email"
                      aria-label={lang === "ar" ? "نسخ البريد" : "Copy email"}
                    >
                      {copied === "email"
                        ? <><Check className="w-3 h-3" />{lang === "ar" ? "تم" : "Copied"}</>
                        : <><Copy className="w-3 h-3" />{lang === "ar" ? "نسخ" : "Copy"}</>}
                    </button>
                  </div>
                </div>
              </li>

              {/* Website */}
              <li className="flex items-start gap-3" data-testid="contact-website-row">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#fdf9ef] border border-[#c9a55c]/40 shrink-0">
                  <Globe className="w-4 h-4 text-[#8a6f2e]" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-[#6b6b6b]">
                    {lang === "ar" ? CARTAL_LABELS.website.ar : CARTAL_LABELS.website.en}
                  </div>
                  <a
                    href={CARTAL_CONTACT.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#0a0a0a] font-semibold hover:text-[#8a6f2e] break-all"
                    data-testid="contact-website-link"
                    dir="ltr"
                  >
                    cartal.sa
                  </a>
                </div>
              </li>

              {/* iter193 — "Headquarters/المقر" temporarily hidden per
                  product request. Keep block intact for easy re-enable
                  by removing this comment + the `false && ` guard. */}
              {false && (
              <li className="flex items-start gap-3">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#fdf9ef] border border-[#c9a55c]/40 shrink-0">
                  <MapPin className="w-4 h-4 text-[#8a6f2e]" />
                </span>
                <div>
                  <div className="text-xs text-[#6b6b6b]">{lang === "ar" ? "المقر" : "Headquarters"}</div>
                  <div className="text-[#0a0a0a] font-semibold leading-snug">
                    {lang === "ar"
                      ? "الجابرية، ينبع الصناعية، المملكة العربية السعودية"
                      : "Al-Jaberiyah, Yanbu Industrial, Saudi Arabia"}
                  </div>
                </div>
              </li>
              )}
              <li className="flex items-start gap-3">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#fdf9ef] border border-[#c9a55c]/40 shrink-0">
                  <Clock className="w-4 h-4 text-[#8a6f2e]" />
                </span>
                <div>
                  <div className="text-xs text-[#6b6b6b]">{lang === "ar" ? "ساعات العمل" : "Business hours"}</div>
                  <div className="text-[#0a0a0a] font-semibold leading-snug">
                    {lang === "ar"
                      ? "الأحد – الخميس · 9 صباحاً – 4 مساءً"
                      : "Sun – Thu · 9 AM – 4 PM"}
                  </div>
                </div>
              </li>
            </ul>

            {/* Direct-action CTAs */}
            <div className="mt-5 flex flex-wrap gap-2">
              <a
                href={CARTAL_CONTACT.whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold transition-colors"
                style={{ backgroundColor: "#25D366", color: "#0a0a0a" }}
                data-testid="cta-whatsapp"
              >
                <MessageSquare className="w-4 h-4" />
                {lang === "ar" ? "تواصل عبر واتساب" : "Chat on WhatsApp"}
              </a>
              <a
                href={CARTAL_CONTACT.supportEmailMailto}
                className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold border-2 transition-colors"
                style={{ borderColor: "#c9a55c", color: "#8a6f2e" }}
                data-testid="cta-email"
              >
                <Mail className="w-4 h-4" />
                {lang === "ar" ? "أرسل بريدًا" : "Send email"}
              </a>
            </div>
          </div>

          {/* ── Social media (official) ── */}
          <div className="surface rounded-3xl p-6" data-testid="social-contact-card">
            <div className="overline overline-gold mb-3">
              {lang === "ar" ? "تابعنا" : "FOLLOW CARTAL"}
            </div>
            <p className="text-xs text-[#6b6b6b] mb-4 leading-relaxed">
              {lang === "ar"
                ? "آخر الإعلانات، إطلاقات الموديلات، والعروض المحدودة أول ما تصل."
                : "New listings, launches and limited offers — the moment they drop."}
            </p>
            <div className="grid grid-cols-2 gap-2">
              <a
                href={CARTAL_CONTACT.tiktokUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-bold border border-[#eee] hover:border-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-white transition-colors"
                data-testid="social-tiktok"
                aria-label="TikTok"
              >
                <BrandIcon name="tiktok" />
                {lang === "ar" ? CARTAL_LABELS.tiktok.ar : CARTAL_LABELS.tiktok.en}
              </a>
              <a
                href={CARTAL_CONTACT.snapchatUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-bold border border-[#eee] hover:border-[#FFFC00] hover:bg-[#FFFC00] hover:text-[#0a0a0a] transition-colors"
                style={{ color: "#0a0a0a" }}
                data-testid="social-snapchat"
                aria-label="Snapchat"
              >
                <BrandIcon name="snapchat" />
                {lang === "ar" ? CARTAL_LABELS.snapchat.ar : CARTAL_LABELS.snapchat.en}
              </a>
              <a
                href={CARTAL_CONTACT.xUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-bold border border-[#eee] hover:border-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-white transition-colors"
                data-testid="social-x"
                aria-label="X (Twitter)"
              >
                <BrandIcon name="x" />
                {lang === "ar" ? CARTAL_LABELS.x.ar : CARTAL_LABELS.x.en}
              </a>
              <a
                href={CARTAL_CONTACT.telegramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-bold border border-[#eee] hover:border-[#0088cc] hover:bg-[#0088cc] hover:text-white transition-colors"
                data-testid="social-telegram"
                aria-label="Telegram"
              >
                <BrandIcon name="telegram" />
                {lang === "ar" ? CARTAL_LABELS.telegram.ar : CARTAL_LABELS.telegram.en}
                <span className="text-[10px] opacity-60" dir="ltr">({CARTAL_CONTACT.telegramHandle})</span>
              </a>
            </div>
          </div>

          {/* ── Disputes & urgent support ── */}
          <div
            className="rounded-3xl p-6"
            style={{ background: "linear-gradient(135deg, #0a0a0a 0%, #1a1f12 100%)", border: "2px solid #c9a55c" }}
          >
            <div className="overline mb-2" style={{ color: "#d4ae6a" }}>
              {lang === "ar" ? "للنزاعات والدعم العاجل" : "DISPUTES & URGENT SUPPORT"}
            </div>
            <p className="text-sm leading-relaxed mb-4" style={{ color: "#e8e1cf" }}>
              {lang === "ar"
                ? "إذا كان لديك نزاع جاري أو مشكلة عاجلة في صفقة، استخدم مركز فض النزاعات للحصول على دعم سريع من الإدارة."
                : "If you have an ongoing dispute or urgent deal issue, use the Dispute Center for fast escalation to the team."}
            </p>
            <Link
              to="/disputes"
              className="inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-bold transition-colors"
              style={{ backgroundColor: "#c9a55c", color: "#0a0a0a" }}
              data-testid="contact-disputes-link"
            >
              {lang === "ar" ? "افتح مركز النزاعات" : "Open Dispute Center"}
            </Link>
          </div>
        </aside>
      </div>
    </div>
  );
}
